import { ReloadOutlined } from "@ant-design/icons";
import { AccelFile } from "@axl-dev/school-sdk";
import cn from "classnames";
import { useMemo } from "react";

import { useLoc } from "@app/shared/lib/hooks";
import { FileIcon } from "@app/shared/ui/FileIcon/FileIcon";
import { Image } from "@app/shared/ui/Image";

import { FileUploadStatus } from "../../FileListItem/FileListItem";
import { FileIconColor, FileListSize, FileListViewType } from "../../Files";
import { FileProgress } from "../FileProgress/FileProgress";

import styles from "./FilePreview.module.scss";

type Props = {
  view: FileListViewType;
  file: AccelFile;
  size: FileListSize;
  progress: number;
  uploadStatus: FileUploadStatus;
  onPreview: () => void;
  onRetryUpload: () => void;
  className?: string;
  fileIconColor: FileIconColor;
};

export const FilePreview: React.FC<Props> = ({
  view,
  file,
  size,
  progress,
  uploadStatus,
  className,
  fileIconColor,
  onPreview,
  onRetryUpload,
}) => {
  const loc = useLoc();
  const fileIconSize = useMemo(() => {
    if (view === "grid") return size;
    switch (size) {
      case "xs":
        return "xs";
      case "sm":
        return "sm";
      case "md":
        return "sm";
      case "lg":
        return "md";
      case "xl":
        return "lg";
      default:
        return size;
    }
  }, [view, size]);

  return (
    <div
      className={cn(
        styles.fl_item__preview,
        "flex flex-center relative w-100 c-pointer",
        className,
      )}
      data-upload-status={uploadStatus}
      data-size={size}
      onClick={() => uploadStatus === FileUploadStatus.Uploaded && onPreview()}
      title={loc.word("File.clickToPreview", { default: "Click to preview" })}
    >
      {file.isImage ? (
        <Image
          fit="cover"
          width="100%"
          height="100%"
          file={file}
          usePreview
          resize={{ w: 100 }}
        />
      ) : (
        <FileIcon
          filename={file.extension}
          size={fileIconSize}
          bgColor={fileIconColor}
        />
      )}

      <div
        className={cn(
          styles.fl_item__preview_background,
          "flex flex-center fs-20 absolute t-0 l-0 r-0 b-0",
        )}
      >
        {uploadStatus === FileUploadStatus.Uploading && (
          <FileProgress view={view} progress={progress} size={size} />
         )}
        {uploadStatus === FileUploadStatus.Error && (
          <div
            title={loc.word("File.retryUploadMsg", { default: "Retry upload" })}
            onClick={() => onRetryUpload()}
          >
            <ReloadOutlined />
          </div>
        )}
      </div>
    </div>
  );
};
